/**
 * Guests API Module
 *
 * API calls for guest (Gast) management.
 * Guests are non-member persons who attend a course session ad-hoc.
 */

import { v4 as uuidv4 } from 'uuid'
import { apiClient } from './client'
import type { PersonDto, AttendanceDto, Gender } from '@/types/api.types'

export const guestsApi = {
  /**
   * Get all guests (non-member persons)
   */
  async getAll(): Promise<PersonDto[]> {
    const response = await apiClient.get<PersonDto[]>('/Persons/guests')
    return response.data
  },

  /**
   * Create a guest person.
   * Uses defaults for required backend fields (dateOfBirth, gender).
   * If no email is provided, a placeholder is generated.
   */
  async createPerson(
    firstName: string,
    lastName: string,
    email?: string,
    phone?: string
  ): Promise<PersonDto> {
    const cleanPhone = phone?.trim().replace(/[\s\-/()]/g, '')
    const data: Record<string, unknown> = {
      firstName,
      lastName,
      dateOfBirth: '1900-01-01',
      gender: 0 as Gender, // Unknown
      email: email || `guest-${uuidv4()}@tus-wannsee.local`,
    }
    // Only send phoneNumber when provided — backend needs fix to accept null/absent
    if (cleanPhone) {
      data['phoneNumber'] = cleanPhone
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[GuestsAPI] POST /Persons payload:', JSON.stringify(data))
    }

    const response = await apiClient.post<PersonDto>('/Persons', data)
    return response.data
  },

  /**
   * Create a guest with attendance in one flow:
   * 1. POST /api/Persons → personId
   * 2. POST /api/Attendances → attendance record
   */
  async createWithAttendance(
    firstName: string,
    lastName: string,
    courseSessionId: number,
    email?: string,
    phone?: string
  ): Promise<{ person: PersonDto; attendance: AttendanceDto }> {
    // Step 1: Create the person
    const person = await guestsApi.createPerson(firstName, lastName, email, phone)

    // Step 2: Create attendance for this person
    // Kein isPresent im Body — das Feld existiert im Backend-DTO nicht mehr (Existenz = Anwesenheit)
    const response = await apiClient.post<AttendanceDto>('/Attendances', {
      personId: person.id,
      courseSessionId,
    })

    return { person, attendance: response.data }
  },

  /**
   * Delete a guest's attendance record
   */
  async removeAttendance(attendanceId: number): Promise<void> {
    await apiClient.delete(`/Attendances/${attendanceId}`)
  },
}
